<script lang="ts">
	import type { PoiCardScheme } from '$lib/components/pois/types';
	import type { ClassValue } from 'svelte/elements';
	import { type Snippet } from 'svelte';

	interface Props {
		href?: string;
		tag?: string;
		class?: ClassValue;
		dataTestId?: `poi-card-${number}` | `poi-card-skeleton-${number}` | 'poi-card';
		isHovered?: boolean;
		category: PoiCardScheme | 'skeleton';
		content?: Snippet;
		footer?: Snippet;
		onmouseenter?: (event: MouseEvent) => void;
		onmouseleave?: (event: MouseEvent) => void;
		onCardClick?: (event: MouseEvent) => void;
		ariaRole?: 'button' | 'link' | 'none';
		layout?: 'vertical' | 'horizontal';
	}

	const {
		href,
		tag = 'div',
		class: classes,
		dataTestId = 'poi-card',
		isHovered = false,
		layout = 'vertical',
		category,
		content,
		footer,
		onmouseenter,
		onmouseleave,
		onCardClick,
		ariaRole = 'none',
	}: Props = $props();
</script>

<!-- eslint-disable svelte/no-navigation-without-resolve -->
<svelte:element
	this={tag}
	data-testid={dataTestId}
	data-category={category}
	class={[
		'poi-card',
		classes,
		'grid text-body border-coloredneutral-weakest bg-neutral-weakest hover:border-transparent border-1 border-solid rd-2 relative',
		category !== 'skeleton' && 'with-shadow',
		isHovered && 'hovered border-transparent',
		layout,
	]}
	role={ariaRole}
	{onmouseenter}
	{onmouseleave}
>
	<a
		{href}
		class={['decoration-none', layout === 'horizontal' ? 'contents' : 'flex flex-col ']}
		data-sveltekit-preload-data="tap"
		onclick={e => onCardClick?.(e)}
		onmousedown={e => e.stopPropagation()}
		onpointerdown={e => e.stopPropagation()}
		ontouchstart={e => e.stopPropagation()}
	>
		{@render content?.()}
	</a>
	{@render footer?.()}
</svelte:element>

<style>
	.poi-card {
		grid-template-rows: 1fr 44px;
		&.hovered::after,
		&.with-shadow:hover::after {
			transition: all 0.15s ease-out;
			opacity: 1;
		}
		&.horizontal {
			grid-template-rows: 1fr;
			grid-template-columns: repeat(2, 1fr);
		}
	}
	.poi-card.horizontal :global(a > *:last-child) {
		grid-column: 2 / 3;
	}
	.poi-card.horizontal :global(a > *:first-child) {
		grid-column: 1 / 2;
		grid-row: 1 / 3;
	}

	.poi-card.horizontal :global(a > figure) {
		height: 144px;
	}

	.poi-card.horizontal :global(a + .button-wrapper) {
		grid-column: 2 / 3;
	}

	.poi-card:hover:not(.no-hover) :global(.button-wrapper > a:not(:disabled)),
	.poi-card:hover:not(.no-hover) :global(.button-wrapper > button:not(:disabled)) {
		--uno: bg-primary-weakest border-primary-weakest;
	}

	.poi-card::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		box-shadow: 0px 30px 80px 0px rgba(0, 0, 0, 0.25);
		border-radius: 8px;
		transition: all 0.15s ease-in;
		z-index: -1;
	}
</style>
